
import React from 'react';
import { Helmet } from 'react-helmet-async';

interface BaseSchema {
  '@context': string;
  '@type': string;
}

interface ArticleSchema extends BaseSchema {
  '@type': 'Article';
  name: string;
  headline: string;
  description?: string;
  image?: string;
  url?: string;
  datePublished?: string;
  dateModified?: string;
  author?: {
    '@type': 'Person';
    name: string;
    image?: string;
  };
  publisher?: {
    '@type': 'Organization';
    name: string;
    logo?: {
      '@type': 'ImageObject';
      url: string;
    };
  };
  mainEntityOfPage?: {
    '@type': 'WebPage';
    '@id': string;
  };
  wordCount?: number;
  articleSection?: string;
  keywords?: string[];
  commentCount?: number;
  articleBody?: string;
}

interface BlogPostingSchema extends BaseSchema {
  '@type': 'BlogPosting';
  name: string;
  headline: string;
  description?: string;
  image?: string;
  url?: string;
  datePublished?: string;
  dateModified?: string;
  author?: {
    '@type': 'Person';
    name: string;
    image?: string;
  };
  publisher?: {
    '@type': 'Organization';
    name: string;
    logo?: {
      '@type': 'ImageObject';
      url: string;
    };
  };
  mainEntityOfPage?: {
    '@type': 'WebPage';
    '@id': string;
  };
  wordCount?: number;
  articleSection?: string;
  keywords?: string[];
  commentCount?: number;
  articleBody?: string;
  isPartOf?: {
    '@type': 'Blog';
    name: string;
    url: string;
  };
}

interface TouristDestinationSchema extends BaseSchema {
  '@type': 'TouristDestination';
  name: string;
  description?: string;
  image?: string;
  url?: string;
  geo?: {
    '@type': 'GeoCoordinates';
    latitude: number;
    longitude: number;
  };
  address?: {
    '@type': 'PostalAddress';
    addressCountry: string;
    addressLocality?: string;
  };
  touristType?: string[];
  includedinHealthAspect?: string;
  priceRange?: string;
  availableLanguage?: string[];
  amenityFeature?: {
    '@type': 'LocationFeatureSpecification';
    name: string;
    value?: boolean | string;
  }[];
  review?: {
    '@type': 'Review';
    reviewRating: {
      '@type': 'Rating';
      ratingValue: string;
      bestRating: string;
    };
    author: {
      '@type': 'Person';
      name: string;
    };
  }[];
  aggregateRating?: {
    '@type': 'AggregateRating';
    ratingValue: string;
    reviewCount: number;
  };
}

interface LandmarkSchema extends BaseSchema {
  '@type': 'LandmarksOrHistoricalBuildings';
  name: string;
  description?: string;
  image?: string;
  url?: string;
  geo?: {
    '@type': 'GeoCoordinates';
    latitude: number;
    longitude: number;
  };
  address?: {
    '@type': 'PostalAddress';
    streetAddress?: string;
    addressLocality?: string;
    addressCountry: string;
  };
  openingHours?: string[];
  isAccessibleForFree?: boolean;
  touristType?: string[];
  maximumAttendeeCapacity?: number;
  review?: {
    '@type': 'Review';
    reviewBody: string;
    reviewRating: {
      '@type': 'Rating';
      ratingValue: string;
    };
    author: {
      '@type': 'Person';
      name: string;
    };
  }[];
}

interface RestaurantSchema extends BaseSchema {
  '@type': 'Restaurant';
  name: string;
  description?: string;
  image?: string;
  url?: string;
  address?: {
    '@type': 'PostalAddress';
    streetAddress?: string;
    addressLocality?: string;
    addressCountry: string;
  };
  geo?: {
    '@type': 'GeoCoordinates';
    latitude: number;
    longitude: number;
  };
  aggregateRating?: {
    '@type': 'AggregateRating';
    ratingValue: number;
    reviewCount: number;
  };
  priceRange?: string;
  servesCuisine?: string[];
  menu?: string;
  acceptsReservations?: boolean;
  openingHours?: string[];
  specialDiet?: string[];
}

interface HotelSchema extends BaseSchema {
  '@type': 'Hotel';
  name: string;
  description?: string;
  image?: string;
  url?: string;
  address?: {
    '@type': 'PostalAddress';
    streetAddress?: string;
    addressLocality?: string;
    addressCountry: string;
  };
  geo?: {
    '@type': 'GeoCoordinates';
    latitude: number;
    longitude: number;
  };
  aggregateRating?: {
    '@type': 'AggregateRating';
    ratingValue: number;
    reviewCount: number;
  };
  priceRange?: string;
  starRating?: number;
  amenities?: string[];
  roomCount?: number;
  checkinTime?: string;
  checkoutTime?: string;
}

interface TouristAttractionSchema extends BaseSchema {
  '@type': 'TouristAttraction';
  name: string;
  description?: string;
  image?: string;
  url?: string;
  address?: {
    '@type': 'PostalAddress';
    streetAddress?: string;
    addressLocality?: string;
    addressCountry: string;
  };
  geo?: {
    '@type': 'GeoCoordinates';
    latitude: number;
    longitude: number;
  };
  touristType?: string[];
}

interface PersonSchema extends BaseSchema {
  '@type': 'Person';
  name: string;
  description?: string;
  image?: string;
  url?: string;
  jobTitle?: string;
  knowsLanguage?: string[];
  address?: {
    '@type': 'PostalAddress';
    addressLocality?: string;
    addressCountry: string;
  };
}

interface WebSiteSchema extends BaseSchema {
  '@type': 'WebSite';
  name: string;
  url: string;
  description?: string;
  potentialAction?: {
    '@type': 'SearchAction';
    target: string;
    'query-input': string;
  };
  author?: {
    '@type': 'Organization' | 'Person';
    name: string;
  };
  publisher?: {
    '@type': 'Organization';
    name: string;
    logo?: {
      '@type': 'ImageObject';
      url: string;
    };
  };
  inLanguage?: string;
  copyrightYear?: number;
  dateModified?: string;
  alternativeName?: string;
}

interface OrganizationSchema extends BaseSchema {
  '@type': 'Organization';
  name: string;
  url: string;
  description?: string;
  logo?: {
    '@type': 'ImageObject';
    url: string;
  };
  sameAs?: string[];
  address?: {
    '@type': 'PostalAddress';
    streetAddress?: string;
    addressLocality?: string;
    addressRegion?: string;
    postalCode?: string;
    addressCountry?: string;
  };
  email?: string;
  telephone?: string;
  foundingDate?: string;
  founders?: {
    '@type': 'Person';
    name: string;
  }[];
  numberOfEmployees?: number;
  contactPoint?: {
    '@type': 'ContactPoint';
    contactType: string;
    telephone: string;
    email?: string;
    availableLanguage?: string[];
  }[];
}

interface FAQPageSchema extends BaseSchema {
  '@type': 'FAQPage';
  mainEntity: {
    '@type': 'Question';
    name: string;
    acceptedAnswer: {
      '@type': 'Answer';
      text: string;
    };
  }[];
}

interface BreadcrumbSchema extends BaseSchema {
  '@type': 'BreadcrumbList';
  itemListElement: {
    '@type': 'ListItem';
    position: number;
    name: string;
    item?: string;
  }[];
}

interface LocalBusinessSchema extends BaseSchema {
  '@type': 'LocalBusiness';
  name: string;
  description?: string;
  image?: string;
  url?: string;
  address?: {
    '@type': 'PostalAddress';
    streetAddress?: string;
    addressLocality?: string;
    addressCountry: string;
  };
  geo?: {
    '@type': 'GeoCoordinates';
    latitude: number;
    longitude: number;
  };
  aggregateRating?: {
    '@type': 'AggregateRating';
    ratingValue: number;
    reviewCount: number;
  };
}

// Union type for all schema types - include FAQPageSchema
type SchemaType = 
  | ArticleSchema
  | BlogPostingSchema
  | TouristDestinationSchema 
  | LandmarkSchema 
  | RestaurantSchema 
  | HotelSchema 
  | TouristAttractionSchema 
  | PersonSchema 
  | WebSiteSchema 
  | OrganizationSchema
  | FAQPageSchema
  | BreadcrumbSchema
  | LocalBusinessSchema;

interface StructuredDataProps {
  schema: SchemaType | SchemaType[];
}

export const StructuredData: React.FC<StructuredDataProps> = ({ schema }) => {
  const schemaArray = Array.isArray(schema) ? schema : [schema];
  
  return (
    <Helmet>
      {schemaArray.map((schemaItem, index) => (
        <script 
          key={index}
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              ...schemaItem
            })
          }}
        />
      ))}
    </Helmet>
  );
};

// Helper functions to create schema objects
export const createTouristDestinationSchema = (data: {
  name: string;
  description?: string;
  image?: string;
  url?: string;
  latitude?: number;
  longitude?: number;
  country: string;
  city?: string;
  touristTypes?: string[];
  priceRange?: string;
  languages?: string[];
  amenities?: {name: string; value?: boolean | string}[];
  reviews?: Array<{rating: number; author: string}>;
  aggregateRating?: {value: number; count: number};
}): TouristDestinationSchema => ({
  '@context': 'https://schema.org',
  '@type': 'TouristDestination',
  name: data.name,
  description: data.description,
  image: data.image,
  url: data.url,
  ...(data.latitude && data.longitude && {
    geo: {
      '@type': 'GeoCoordinates',
      latitude: data.latitude,
      longitude: data.longitude
    }
  }),
  address: {
    '@type': 'PostalAddress',
    addressCountry: data.country,
    ...(data.city && { addressLocality: data.city })
  },
  ...(data.touristTypes && { touristType: data.touristTypes }),
  ...(data.priceRange && { priceRange: data.priceRange }),
  ...(data.languages && { availableLanguage: data.languages }),
  ...(data.amenities && { 
    amenityFeature: data.amenities.map(amenity => ({
      '@type': 'LocationFeatureSpecification',
      name: amenity.name,
      ...(amenity.value !== undefined && { value: amenity.value })
    }))
  }),
  ...(data.reviews && data.reviews.length > 0 && {
    review: data.reviews.map(review => ({
      '@type': 'Review',
      reviewRating: {
        '@type': 'Rating',
        ratingValue: String(review.rating),
        bestRating: '5'
      },
      author: {
        '@type': 'Person',
        name: review.author
      }
    }))
  }),
  ...(data.aggregateRating && {
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: String(data.aggregateRating.value),
      reviewCount: data.aggregateRating.count
    }
  })
});

export const createRestaurantSchema = (data: {
  name: string;
  description?: string;
  image?: string;
  url?: string;
  address?: string;
  city?: string;
  country: string;
  latitude?: number;
  longitude?: number;
  rating?: number;
  reviewCount?: number;
  priceRange?: string;
  cuisineTypes?: string[];
  servesCuisine?: string[];
  menu?: string;
  acceptsReservations?: boolean;
  openingHours?: string[];
  specialDiet?: string[];
}): RestaurantSchema => ({
  '@context': 'https://schema.org',
  '@type': 'Restaurant',
  name: data.name,
  description: data.description,
  image: data.image,
  url: data.url,
  address: {
    '@type': 'PostalAddress',
    streetAddress: data.address,
    addressLocality: data.city,
    addressCountry: data.country
  },
  ...(data.latitude && data.longitude && {
    geo: {
      '@type': 'GeoCoordinates',
      latitude: data.latitude,
      longitude: data.longitude
    }
  }),
  ...(data.rating && data.reviewCount && {
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: data.rating,
      reviewCount: data.reviewCount
    }
  }),
  ...(data.priceRange && { priceRange: data.priceRange }),
  ...(data.servesCuisine && { servesCuisine: data.servesCuisine }),
  ...(data.menu && { menu: data.menu }),
  ...(data.acceptsReservations !== undefined && { acceptsReservations: data.acceptsReservations }),
  ...(data.openingHours && { openingHours: data.openingHours }),
  ...(data.specialDiet && { specialDiet: data.specialDiet })
});

export const createHotelSchema = (data: {
  name: string;
  description?: string;
  image?: string;
  url?: string;
  address?: string;
  city?: string;
  country: string;
  latitude?: number;
  longitude?: number;
  rating?: number;
  reviewCount?: number;
  priceRange?: string;
  starRating?: number;
  amenities?: string[];
  roomCount?: number;
  checkinTime?: string;
  checkoutTime?: string;
}): HotelSchema => ({
  '@context': 'https://schema.org',
  '@type': 'Hotel',
  name: data.name,
  description: data.description,
  image: data.image,
  url: data.url,
  address: {
    '@type': 'PostalAddress',
    streetAddress: data.address,
    addressLocality: data.city,
    addressCountry: data.country
  },
  ...(data.latitude && data.longitude && {
    geo: {
      '@type': 'GeoCoordinates',
      latitude: data.latitude,
      longitude: data.longitude
    }
  }),
  ...(data.rating && data.reviewCount && {
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: data.rating,
      reviewCount: data.reviewCount
    }
  }),
  ...(data.priceRange && { priceRange: data.priceRange }),
  ...(data.starRating && { starRating: data.starRating }),
  ...(data.amenities && { amenities: data.amenities }),
  ...(data.roomCount && { roomCount: data.roomCount }),
  ...(data.checkinTime && { checkinTime: data.checkinTime }),
  ...(data.checkoutTime && { checkoutTime: data.checkoutTime })
});

export const createPersonSchema = (data: {
  name: string;
  description?: string;
  image?: string;
  url?: string;
  jobTitle?: string;
  languages?: string[];
  city?: string;
  country: string;
}): PersonSchema => ({
  '@context': 'https://schema.org',
  '@type': 'Person',
  name: data.name,
  description: data.description,
  image: data.image,
  url: data.url,
  jobTitle: data.jobTitle,
  knowsLanguage: data.languages,
  address: {
    '@type': 'PostalAddress',
    addressLocality: data.city,
    addressCountry: data.country
  }
});

export const createWebSiteSchema = (data?: {
  name?: string;
  description?: string;
  keywords?: string[];
  language?: string;
  copyrightYear?: number;
  lastModified?: string;
}): WebSiteSchema => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: data?.name || 'travilis',
  url: typeof window !== 'undefined' ? window.location.origin : '',
  description: data?.description || 'Connect with local hosts, discover amazing destinations and plan your perfect journey with travilis',
  inLanguage: data?.language || 'en-US',
  copyrightYear: data?.copyrightYear || new Date().getFullYear(),
  dateModified: data?.lastModified || new Date().toISOString(),
  alternativeName: 'Travilis - Your Travel Companion',
  potentialAction: {
    '@type': 'SearchAction',
    target: `${typeof window !== 'undefined' ? window.location.origin : ''}/search?q={search_term_string}`,
    'query-input': 'required name=search_term_string'
  },
  publisher: {
    '@type': 'Organization',
    name: 'travilis',
    logo: {
      '@type': 'ImageObject',
      url: `${typeof window !== 'undefined' ? window.location.origin : ''}/t-favicon.svg`
    }
  }
});

export const createOrganizationSchema = (data?: {
  foundingDate?: string;
  email?: string;
  phone?: string;
  address?: {
    street?: string;
    city?: string;
    region?: string;
    postal?: string;
    country?: string;
  };
  socialProfiles?: string[];
}): OrganizationSchema => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'travilis',
  url: typeof window !== 'undefined' ? window.location.origin : '',
  description: 'Your travel companion - connecting travelers with local hosts worldwide',
  logo: {
    '@type': 'ImageObject',
    url: `${typeof window !== 'undefined' ? window.location.origin : ''}/t-favicon.svg`
  },
  foundingDate: data?.foundingDate || '2023',
  email: data?.email || 'contact@travilis.com',
  telephone: data?.phone || '',
  sameAs: data?.socialProfiles || [
    'https://twitter.com/travilis',
    'https://www.facebook.com/travilis',
    'https://www.instagram.com/travilis/'
  ],
  ...(data?.address && {
    address: {
      '@type': 'PostalAddress',
      streetAddress: data.address.street || '',
      addressLocality: data.address.city || '',
      addressRegion: data.address.region || '',
      postalCode: data.address.postal || '',
      addressCountry: data.address.country || 'US'
    }
  }),
  contactPoint: [{
    '@type': 'ContactPoint',
    contactType: 'customer service',
    telephone: data?.phone || '',
    email: data?.email || 'support@travilis.com',
    availableLanguage: ['English', 'Spanish', 'French']
  }]
});

export const createArticleSchema = (data: {
  name: string;
  headline: string;
  description?: string;
  image?: string;
  url?: string;
  datePublished?: string;
  dateModified?: string;
  authorName?: string;
  authorImage?: string;
  wordCount?: number;
  section?: string;
  keywords?: string[];
  commentCount?: number;
  articleBody?: string;
  isPartOf?: string;
  isBlogPosting?: boolean;
}): ArticleSchema | BlogPostingSchema => {
  const baseSchema = {
    '@context': 'https://schema.org' as const,
    name: data.name,
    headline: data.headline,
    description: data.description,
    image: data.image,
    url: data.url,
    datePublished: data.datePublished,
    dateModified: data.dateModified || data.datePublished,
    wordCount: data.wordCount,
    articleSection: data.section,
    keywords: data.keywords,
    commentCount: data.commentCount,
    articleBody: data.articleBody?.substring(0, 500),
    ...(data.authorName && {
      author: {
        '@type': 'Person' as const,
        name: data.authorName,
        ...(data.authorImage && { image: data.authorImage })
      }
    }),
    publisher: {
      '@type': 'Organization' as const,
      name: 'travilis',
      logo: {
        '@type': 'ImageObject' as const,
        url: `${typeof window !== 'undefined' ? window.location.origin : ''}/t-favicon.svg`
      }
    },
    mainEntityOfPage: {
      '@type': 'WebPage' as const,
      '@id': data.url || (typeof window !== 'undefined' ? window.location.href : '')
    }
  };
  
  if (data.isBlogPosting) {
    return {
      ...baseSchema,
      '@type': 'BlogPosting' as const,
      ...(data.isPartOf && {
        isPartOf: {
          '@type': 'Blog' as const,
          name: 'Travilis Blog',
          url: `${typeof window !== 'undefined' ? window.location.origin : ''}/blogs`
        }
      })
    } as BlogPostingSchema;
  }
  
  return {
    ...baseSchema,
    '@type': 'Article' as const
  } as ArticleSchema;
};

export const createFAQSchema = (faqs: Array<{question: string; answer: string}>): FAQPageSchema => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map(faq => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer
    }
  }))
});

export const createBreadcrumbSchema = (items: Array<{name: string; url?: string}>): BreadcrumbSchema => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    ...(item.url && { item: item.url.startsWith('http') ? item.url : `${typeof window !== 'undefined' ? window.location.origin : ''}${item.url}` })
  }))
});

export const createLocalBusinessSchema = (data: {
  name: string;
  description?: string;
  image?: string;
  url?: string;
  address?: string;
  city?: string;
  country: string;
  latitude?: number;
  longitude?: number;
  rating?: number;
  reviewCount?: number;
}): LocalBusinessSchema => ({
  '@context': 'https://schema.org',
  '@type': 'LocalBusiness',
  name: data.name,
  description: data.description,
  image: data.image,
  url: data.url,
  address: {
    '@type': 'PostalAddress',
    streetAddress: data.address,
    addressLocality: data.city,
    addressCountry: data.country
  },
  ...(data.latitude && data.longitude && {
    geo: {
      '@type': 'GeoCoordinates',
      latitude: data.latitude,
      longitude: data.longitude
    }
  }),
  ...(data.rating && data.reviewCount && {
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: data.rating,
      reviewCount: data.reviewCount
    }
  })
});
